// @ts-check

import { resolveRouteWithId, routerUrl } from '../constants/router-url';

/**
 * Clone a campaign and navigate to the new campaign Details page.
 *
 * Intended to centralize the common flow used across multiple pages:
 * - Clear existing banner alerts.
 * - Dispatch the clone thunk (which owns error/banner handling).
 * - Close the clone modal and optionally run caller-provided post-success work.
 * - Reset preview state.
 * - Navigate to Details, passing a one-shot navigation state flag so Details can.
 *   Auto-refresh the cloned campaign (e.g., re-run the query and update the page fields as needed).
 *
 * @param {object} params
 * @param {(action: any) => any} params.dispatch Redux dispatch.
 * @param {any} params.history React Router history.
 * @param {string} params.sourceCampaignId Campaign id to clone from.
 * @param {string} params.cloneName New campaign name.
 * @param {(payload: { cloneFromCampaignId: string, name: string }) => any} params.cloneCampaignAsync Clone thunk action creator.
 * @param {() => any} params.resetAlertMessages Action creator to clear banner alerts.
 * @param {() => any} params.resetCampaignPreview Action creator to clear preview cache/state.
 * @param {() => void} [params.onClose] Called after a successful clone, before navigation.
 * @param {(newCampaignId: string) => void} [params.onAfterCloneSuccess] Optional post-success callback (e.g., reset page state).
 * @param {any} [params.navigationState] Optional navigation state passed to the selected history navigation method (`push` or `replace`).
 * @param {'push'|'replace'} [params.navigationMethod] Which history method to use when navigating to Details.
 * @returns {Promise<string|null|undefined>} New campaign id when successful.
 */
export async function cloneCampaignAndNavigateToDetails(params) {
  const {
    dispatch,
    history,
    sourceCampaignId,
    cloneName,
    cloneCampaignAsync,
    resetAlertMessages,
    resetCampaignPreview,
    onClose,
    onAfterCloneSuccess,
    navigationState,
    navigationMethod = 'push',
  } = params;

  if (!sourceCampaignId) return;

  dispatch(resetAlertMessages());

  // `cloneCampaignAsync` owns banner error state and resolves with:
  // - `string` on success (new campaign id)
  // - `null` when the API response is missing an id
  // - `undefined` when an exception occurs and is handled inside the thunk
  const newCampaignId = await dispatch(
    cloneCampaignAsync({
      cloneFromCampaignId: sourceCampaignId,
      name: cloneName,
    }),
  ).unwrap();

  if (!newCampaignId) return newCampaignId;

  onClose?.();
  onAfterCloneSuccess?.(newCampaignId);

  dispatch(resetCampaignPreview());

  const url = resolveRouteWithId(routerUrl.CAMPAIGN_DETAILS, newCampaignId);

  const navigateToDetails =
    navigationMethod === 'replace' ? history.replace : history.push;
  navigateToDetails(url, navigationState ?? { refreshAfterClone: true });

  return newCampaignId;
}
